import ApiMethods from 'shared/api/api-methods';
import { paramsToQueryString } from 'shared/lib/util';
import type {
  Balance,
  BalanceDetails,
  BalancePayments,
  IBalanceSchema,
  RecurrentPayment,
} from '@/enteties/balance/model';
import { queryOptions } from '@tanstack/react-query';
import { balanceKeys } from '@/enteties/balance/api/balance.query-keys';
import type { StatusResponse } from 'shared/api/services/types';

const url = '/balance';
const paymentUrl = '/payment';

export async function getBalance(): Promise<Balance | null> {
  const data = await ApiMethods.get(url);
  if (!data.ok) return null;
  return await data.json();
}

export async function topUpBalance(
  data: IBalanceSchema
): Promise<{ redirectUrl: string }> {
  const response = await ApiMethods.post(`${url}/topup`, data);
  if (!response.ok) throw Error(`${response.status}`);
  return await response.json();
}

// AUTOPAYMENT

export const getAutopayment = {
  fetchAutoPayments:
    async function fetchAutoPayments(): Promise<RecurrentPayment | null> {
      const data = await ApiMethods.get(`${paymentUrl}/recurrent_payment`);
      if (!data.ok) return null;
      return await data.json();
    },
  getAutopaymentQueryOptions: () =>
    queryOptions({
      queryKey: balanceKeys.recurrentPayment,
      queryFn: () => getAutopayment.fetchAutoPayments(),
    }),
};

export const deleteAutopayment = async (): Promise<StatusResponse> => {
  const data = await ApiMethods.delete(`${paymentUrl}/recurrent_payment`);
  if (!data.ok) throw Error(`${data.status}`);
  return { status: 'OK' };
};

// PROMOS

export async function getPromos(): Promise<string[]> {
  const data = await ApiMethods.get(`/promo/used`);
  if (!data.ok) throw Error(`${data.status}`);
  return await data.json();
}

export async function addNewPromo(
  code: string
): Promise<{ description: string }> {
  const data = await ApiMethods.post(`/promo`, { code });
  if (!data.ok) throw Error(`${data.status}`);
  return await data.json();
}

// BALANCE DETAILS

export async function getBalanceDetails(params: {
  from: string;
  to: string;
  page: number;
  size: number;
}): Promise<BalanceDetails[]> {
  const data = await ApiMethods.get(
    `${url}/details${paramsToQueryString(params)}`
  );
  if (!data.ok) throw Error(`${data.status}`);
  return await data.json();
}

export async function getBalancePayments(params: {
  from: string;
  to: string;
  page: number;
  size: number;
}): Promise<BalancePayments[]> {
  const data = await ApiMethods.get(
    `${paymentUrl}/payments${paramsToQueryString(params)}`
  );
  if (!data.ok) throw Error(`${data.status}`);
  return await data.json();
}
